import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import {
  Pencil,
  Plus,
  Trash2,
  X,
  Check,
  Search,
  Lock,
  Sparkles,
  Inbox,
} from 'lucide-react'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Skeleton } from './ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import {
  useGetSkillsQuery,
  useCreateSkillMutation,
  useUpdateSkillMutation,
  useDeleteSkillMutation,
  type Skill,
} from '@/redux/service/skills'
import { useCheckPermission } from '@/hooks/useCheckPermission'

const isSystemRow = (s: Skill) => s.is_system === 1 || s.is_system === true

const SkillsManager = () => {
  const { data, isLoading } = useGetSkillsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  })
  const [createSkill, { isLoading: isCreating }] = useCreateSkillMutation()
  const [updateSkill] = useUpdateSkillMutation()
  const [deleteSkill, { isLoading: isDeleting }] = useDeleteSkillMutation()
  const { hasPageAccess } = useCheckPermission()
  const canMutate = hasPageAccess('employees')

  const [newSkill, setNewSkill] = useState('')
  const [search, setSearch] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingName, setEditingName] = useState('')
  const [confirmDelete, setConfirmDelete] = useState<Skill | null>(null)

  const skills = useMemo(() => data?.data ?? [], [data])
  const { systemSkills, userSkills } = useMemo(() => {
    const q = search.trim().toLowerCase()
    const matches = (s: Skill) => !q || s.name.toLowerCase().includes(q)
    const sys: Skill[] = []
    const usr: Skill[] = []
    for (const s of skills) {
      if (!matches(s)) continue
      if (isSystemRow(s)) sys.push(s)
      else usr.push(s)
    }
    sys.sort((a, b) => a.name.localeCompare(b.name))
    usr.sort((a, b) => a.name.localeCompare(b.name))
    return { systemSkills: sys, userSkills: usr }
  }, [skills, search])

  const systemCount = skills.filter(isSystemRow).length
  const userCount = skills.length - systemCount

  const handleCreate = async () => {
    const name = newSkill.trim()
    if (!name) return
    try {
      await createSkill({ name }).unwrap()
      setNewSkill('')
      toast.success('Skill added')
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } }
      toast.error(e?.data?.message ?? 'Failed to add skill')
    }
  }

  const startEdit = (s: Skill) => {
    setEditingId(s.id)
    setEditingName(s.name)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditingName('')
  }

  const saveEdit = async (id: number) => {
    const name = editingName.trim()
    if (!name) return
    try {
      await updateSkill({ id, name }).unwrap()
      cancelEdit()
      toast.success('Skill updated')
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } }
      toast.error(e?.data?.message ?? 'Failed to update skill')
    }
  }

  const handleDelete = async () => {
    if (!confirmDelete) return
    try {
      await deleteSkill(confirmDelete.id).unwrap()
      toast.success('Skill deleted')
      setConfirmDelete(null)
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } }
      toast.error(e?.data?.message ?? 'Failed to delete skill')
    }
  }

  return (
    <section className='bg-card border border-border rounded-xl text-foreground overflow-hidden'>
      {/* Header */}
      <div className='px-6 py-5 border-b border-border'>
        <div className='flex items-start justify-between gap-4 flex-wrap'>
          <div>
            <h2 className='text-lg font-semibold text-foreground'>
              Skills Library
            </h2>
            <p className='text-sm text-muted-foreground mt-0.5'>
              Manage the skill suggestions shown when assigning employees.
              System skills are read-only.
            </p>
          </div>
          <div className='flex items-center gap-2'>
            <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-muted text-muted-foreground border border-border'>
              <Lock className='w-3 h-3' />
              {systemCount} System
            </span>
            <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-primary/10 text-primary border border-primary/20'>
              <Sparkles className='w-3 h-3' />
              {userCount} Custom
            </span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className='px-6 py-4 border-b border-border flex flex-wrap items-center gap-3 bg-muted/30'>
        {canMutate && (
          <div className='flex gap-2 flex-1 min-w-[260px] max-w-md'>
            <Input
              value={newSkill}
              onChange={e => setNewSkill(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleCreate()
                }
              }}
              placeholder='Add a new skill'
              className='h-9'
            />
            <Button
              type='button'
              onClick={handleCreate}
              disabled={isCreating || !newSkill.trim()}
              className='h-9 gap-1.5'
            >
              <Plus className='w-4 h-4' />
              Add
            </Button>
          </div>
        )}
        <div className='relative flex-1 min-w-[220px] max-w-md ml-auto'>
          <Search className='w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground' />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder='Search skills'
            className='pl-9 h-9'
          />
        </div>
      </div>

      {/* Body */}
      <div className='p-6'>
        {isLoading ? (
          <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>
            {Array.from({ length: 2 }).map((_, c) => (
              <div
                key={c}
                className='border border-border rounded-xl p-3 space-y-2 bg-card'
              >
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className='h-9 w-full rounded-md' />
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>
            <SkillColumn
              title='System Skills'
              subtitle='Built-in suggestions, cannot be edited.'
              icon={<Lock className='w-3.5 h-3.5' />}
              accent='muted'
              count={systemSkills.length}
              totalCount={systemCount}
              search={search}
              emptyText='No system skills match your search.'
              skills={systemSkills}
              canMutate={false}
              editingId={null}
              editingName=''
              setEditingName={() => {}}
              onStartEdit={() => {}}
              onSaveEdit={() => {}}
              onCancelEdit={() => {}}
              onDelete={() => {}}
            />
            <SkillColumn
              title='Custom Skills'
              subtitle='Skills your team has added.'
              icon={<Sparkles className='w-3.5 h-3.5' />}
              accent='primary'
              count={userSkills.length}
              totalCount={userCount}
              search={search}
              emptyText='No custom skills yet — add one above or save a new skill on an employee.'
              skills={userSkills}
              canMutate={canMutate}
              editingId={editingId}
              editingName={editingName}
              setEditingName={setEditingName}
              onStartEdit={startEdit}
              onSaveEdit={saveEdit}
              onCancelEdit={cancelEdit}
              onDelete={s => setConfirmDelete(s)}
            />
          </div>
        )}
      </div>

      <Dialog
        open={!!confirmDelete}
        onOpenChange={open => !open && setConfirmDelete(null)}
      >
        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle>Delete skill</DialogTitle>
            <DialogDescription>
              Delete{' '}
              <span className='font-semibold'>{confirmDelete?.name}</span>? It
              will be removed from the suggestions list. Employees that already
              have this skill will keep it. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant='destructive'
              disabled={isDeleting}
              onClick={handleDelete}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}

interface SkillColumnProps {
  title: string
  subtitle: string
  icon: React.ReactNode
  accent: 'muted' | 'primary'
  count: number
  totalCount: number
  search: string
  emptyText: string
  skills: Skill[]
  canMutate: boolean
  editingId: number | null
  editingName: string
  setEditingName: (v: string) => void
  onStartEdit: (s: Skill) => void
  onSaveEdit: (id: number) => void
  onCancelEdit: () => void
  onDelete: (s: Skill) => void
}

const SkillColumn = ({
  title,
  subtitle,
  icon,
  accent,
  count,
  totalCount,
  search,
  emptyText,
  skills,
  canMutate,
  editingId,
  editingName,
  setEditingName,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
}: SkillColumnProps) => {
  const headerAccent =
    accent === 'primary'
      ? 'border-primary/20 bg-primary/5'
      : 'border-border bg-muted/40'
  const dotAccent =
    accent === 'primary' ? 'bg-primary' : 'bg-muted-foreground/40'
  const iconAccent =
    accent === 'primary' ? 'text-primary' : 'text-muted-foreground'
  const badgeAccent =
    accent === 'primary'
      ? 'bg-primary/10 text-primary border-primary/20'
      : 'bg-background text-muted-foreground border-border'
  const isFiltered = search.trim().length > 0 && count !== totalCount

  return (
    <div className='border border-border rounded-xl overflow-hidden flex flex-col bg-card min-h-[320px]'>
      <div
        className={`flex items-center justify-between px-4 py-3 border-b ${headerAccent}`}
      >
        <div className='flex items-center gap-2 min-w-0'>
          <span className={iconAccent}>{icon}</span>
          <div className='min-w-0'>
            <h3 className='text-sm font-semibold text-foreground leading-tight'>
              {title}
            </h3>
            <p className='text-[11px] text-muted-foreground truncate'>
              {subtitle}
            </p>
          </div>
        </div>
        <span
          className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border shrink-0 ${badgeAccent}`}
        >
          {isFiltered ? `${count} / ${totalCount}` : count}
        </span>
      </div>

      {skills.length === 0 ? (
        <div className='flex-1 flex flex-col items-center justify-center text-center px-6 py-10 gap-2'>
          <Inbox className='w-8 h-8 text-muted-foreground/40' />
          <p className='text-xs text-muted-foreground max-w-[260px]'>
            {emptyText}
          </p>
        </div>
      ) : (
        <ul className='flex-1 max-h-[60vh] overflow-y-auto'>
          {skills.map(skill => {
            const isEditing = editingId === skill.id
            return (
              <li
                key={skill.id}
                className='group flex items-center gap-2 px-4 h-10 border-b border-white/10 last:border-b-0 hover:bg-muted/60 transition-colors'
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotAccent}`}
                />
                <div className='flex-1 min-w-0'>
                  {isEditing ? (
                    <Input
                      value={editingName}
                      onChange={e => setEditingName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          onSaveEdit(skill.id)
                        } else if (e.key === 'Escape') {
                          onCancelEdit()
                        }
                      }}
                      autoFocus
                      className='h-7 text-sm'
                    />
                  ) : (
                    <span
                      className='text-sm text-foreground truncate block'
                      title={skill.name}
                    >
                      {skill.name}
                    </span>
                  )}
                </div>

                {canMutate && !isEditing && (
                  <div className='flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity'>
                    <button
                      type='button'
                      onClick={() => onStartEdit(skill)}
                      className='p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors'
                      title='Edit'
                    >
                      <Pencil className='w-3.5 h-3.5' />
                    </button>
                    <button
                      type='button'
                      onClick={() => onDelete(skill)}
                      className='p-1.5 rounded-md hover:bg-destructive/10 text-destructive transition-colors'
                      title='Delete'
                    >
                      <Trash2 className='w-3.5 h-3.5' />
                    </button>
                  </div>
                )}

                {isEditing && (
                  <div className='flex items-center gap-0.5'>
                    <button
                      type='button'
                      onClick={() => onSaveEdit(skill.id)}
                      className='p-1.5 rounded-md hover:bg-primary/10 text-primary transition-colors'
                      title='Save'
                    >
                      <Check className='w-3.5 h-3.5' />
                    </button>
                    <button
                      type='button'
                      onClick={onCancelEdit}
                      className='p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors'
                      title='Cancel'
                    >
                      <X className='w-3.5 h-3.5' />
                    </button>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default SkillsManager
